import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, UserMinus, Users } from "lucide-react";

export function GroupMembersDialog({ thread, recipients, meId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [title, setTitle] = useState(thread.title || "");
  const rename = () => { const t = title.trim(); if (t && t !== thread.title) change({ title: t }, "Group renamed"); };
  const memberIds = new Set([meId, ...(thread.members || []).map((m) => m.id)]);
  const candidates = recipients.filter((r) => !memberIds.has(r.id) && `${r.name} ${r.company || ""}`.toLowerCase().includes(q.toLowerCase()));
  const change = async (patch, label) => {
    setBusy(label);
    try { await api.patch(`/threads/${thread.id}/members`, patch); toast.success(label); await onChanged(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };
  return (
    <>
      <Button variant="outline" size="sm" className="h-8" onClick={() => setOpen(true)} data-testid="group-members-button"><Users className="h-3.5 w-3.5 mr-1" />People</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="group-members-dialog">
          <DialogHeader><DialogTitle>People in "{thread.title}"</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="tag-mono text-slate-500 mb-1">GROUP NAME</div>
              <div className="flex gap-2"><Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} onKeyDown={(e) => e.key === "Enter" && rename()} data-testid="group-rename-input" /><Button size="sm" variant="outline" onClick={rename} disabled={!!busy || !title.trim() || title.trim() === thread.title} data-testid="group-rename-button">Rename</Button></div>
            </div>
            <div>
              <div className="tag-mono text-slate-500 mb-1">IN THIS CONVERSATION</div>
              <ul className="divide-y divide-slate-100 rounded border border-slate-200 text-sm">
                <li className="flex items-center justify-between px-2 py-1.5"><span><b>You</b></span><span className="text-xs text-slate-400">owner</span></li>
                {(thread.members || []).map((m) => (
                  <li key={m.id} className="flex items-center justify-between px-2 py-1.5" data-testid={`group-member-${m.id}`}>
                    <span className="truncate"><b>{m.name}</b> <span className="text-slate-500 text-xs">· {m.role === "admin" ? "Admin" : m.company || m.email}</span></span>
                    <button type="button" disabled={!!busy} onClick={() => change({ remove: [m.id] }, `Removed ${m.name}`)} className="text-slate-400 hover:text-rose-600 inline-flex items-center gap-1 text-xs" data-testid={`group-remove-${m.id}`}><UserMinus className="h-3.5 w-3.5" />Remove</button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><div className="tag-mono text-slate-500">ADD SOMEONE</div><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-7 w-40 text-xs" data-testid="group-add-search" /></div>
              <ul className="max-h-44 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-200 text-sm">
                {candidates.map((r) => (
                  <li key={r.id} className="flex items-center justify-between px-2 py-1.5">
                    <span className="truncate"><b>{r.name}</b> <span className="text-slate-500 text-xs">· {r.role === "admin" ? "Admin" : r.role === "terminal_manager" ? `${r.terminal_name || "Terminal manager"} (${r.company})` : r.shared ? `${r.company} · team member` : r.company || r.email}</span></span>
                    <button type="button" disabled={!!busy} onClick={() => change({ add: [r.id] }, `Added ${r.name}`)} className="text-blue-700 hover:underline inline-flex items-center gap-1 text-xs" data-testid={`group-add-${r.id}`}><UserPlus className="h-3.5 w-3.5" />Add</button>
                  </li>
                ))}
                {candidates.length === 0 && <li className="p-3 text-xs text-slate-400">Nobody else to add</li>}
              </ul>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
